import { useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp, Circle, Clock, Flag, Lock, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Modal } from "@/components/ui/Modal";
import { formatDate } from "@/utils/formatDate";
import {
  useAddOrderMilestone,
  useContractorCompleteMilestone,
  useDeleteOrderMilestone,
  useMoveOrderMilestone,
  useUpdateOrderMilestone,
} from "../hooks/useOrders";
import type { OrderMilestone, OrderMilestoneActionKind, OrderMilestoneEvent } from "../types";

const EVENT_ICON: Record<OrderMilestoneActionKind, typeof CheckCircle2> = {
  created: Plus,
  completed: CheckCircle2,
  reopened: RotateCcw,
  edited: Pencil,
  deleted: Trash2,
};
const EVENT_TONE: Record<OrderMilestoneActionKind, string> = {
  created: "text-blue-600",
  completed: "text-green-600",
  reopened: "text-amber-600",
  edited: "text-gray-500",
  deleted: "text-red-600",
};

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/**
 * Kamienie milowe zamówienia — terminy pośrednie "nazwa → data". Read-only for the
 * contractor; internal users (`canManage`) add ad-hoc milestones, mark them done, edit
 * dates, and delete non-principal ones. Predefined ZE checkpoints are `is_principal`
 * (locked — no delete). `canEditList=false` (finished / closed / cancelled order) freezes
 * the list: no add form, no delete buttons, no reordering — ticking and dates stay
 * editable.
 * `filterable` (contractor's order view) adds a status filter over the list:
 * all (default) / in progress / done.
 * `canComplete` (the contractor's own order page, and the same page in execution mode
 * for a GK employee with a „Realizacja" grant) lets the viewer mark an open milestone
 * done through the same confirm modal and endpoint as „Moje terminy" (POST …/complete/).
 * No undo — reopening stays with the orderer. A done milestone shows the completion
 * date and who ticked it (`completed_by_name`) to every viewer.
 */
export function OrderMilestonesSection({
  orderId,
  milestones,
  events = [],
  canManage,
  canEditList = canManage,
  filterable = false,
  canComplete = false,
}: {
  orderId: string;
  milestones: OrderMilestone[];
  events?: OrderMilestoneEvent[];
  canManage: boolean;
  canEditList?: boolean;
  filterable?: boolean;
  canComplete?: boolean;
}) {
  const [label, setLabel] = useState("");
  const [due, setDue] = useState("");
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "open" | "done">("all");
  const add = useAddOrderMilestone(orderId);
  const update = useUpdateOrderMilestone(orderId);
  const remove = useDeleteOrderMilestone(orderId);
  const move = useMoveOrderMilestone(orderId);
  // Wykonujący (grant „Realizacja"): oznaczenie zawsze przez modal — jak w „Moich
  // terminach" podwykonawcy; cofnąć wykonanie może już tylko zlecający.
  const complete = useContractorCompleteMilestone();
  const [confirmMilestone, setConfirmMilestone] = useState<OrderMilestone | null>(null);
  const closeConfirm = () => {
    if (complete.isPending) return;
    setConfirmMilestone(null);
    complete.reset();
  };
  const confirmComplete = () => {
    if (!confirmMilestone) return;
    complete.mutate(
      { orderId, milestoneId: confirmMilestone.id },
      { onSuccess: () => setConfirmMilestone(null) },
    );
  };
  const executorCanTick = canComplete && !canManage;

  if (!canManage && !milestones.length && !events.length) return null;

  const handleAdd = async () => {
    if (!label.trim() || !due) return;
    setError("");
    try {
      await add.mutateAsync({ label: label.trim(), due_date: due });
      setLabel("");
      setDue("");
    } catch {
      setError("Nie udało się dodać kamienia milowego.");
    }
  };

  const toggleDone = (m: OrderMilestone) =>
    update.mutate({
      milestoneId: m.id,
      payload: { completed_at: m.is_done ? null : new Date().toISOString().slice(0, 10) },
    });

  const changeDue = (m: OrderMilestone, due_date: string) => {
    if (!due_date || due_date === m.due_date) return;
    update.mutate({ milestoneId: m.id, payload: { due_date } });
  };

  const handleDelete = (m: OrderMilestone) => {
    if (!window.confirm(`Usunąć kamień milowy „${m.label}”?`)) return;
    remove.mutate(m.id);
  };

  const today = new Date().toISOString().slice(0, 10);

  // Filtr tylko po stronie klienta; bez `filterable` lista jest pełna, więc indeksy
  // przycisków „w górę / w dół" (GK) nadal odpowiadają kolejności kamieni.
  const visible =
    filterable && statusFilter !== "all"
      ? milestones.filter((m) => (statusFilter === "done" ? m.is_done : !m.is_done))
      : milestones;

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 mb-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
          <Flag size={14} /> Kamienie milowe ({milestones.length})
        </p>
        {filterable && milestones.length > 0 && (
          <select
            aria-label="Filtr kamieni milowych"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="text-xs border border-gray-200 dark:border-gray-600 px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          >
            <option value="all">Kamienie: wszystkie</option>
            <option value="open">Kamienie: w trakcie</option>
            <option value="done">Kamienie: wykonane</option>
          </select>
        )}
      </div>

      {milestones.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak kamieni milowych.</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak kamieni o wybranym statusie.</p>
      ) : (
        <ul className="space-y-1.5">
          {visible.map((m, i) => {
            const overdue = !m.is_done && m.due_date < today;
            return (
              <li
                key={m.id}
                className="flex items-center gap-2 text-sm bg-white dark:bg-gray-900/40 px-2.5 py-1.5 rounded border border-gray-200 dark:border-gray-700"
              >
                <button
                  type="button"
                  disabled={
                    executorCanTick
                      ? m.is_done || complete.isPending
                      : !canManage || update.isPending
                  }
                  onClick={() => (executorCanTick ? setConfirmMilestone(m) : toggleDone(m))}
                  className="shrink-0 text-gray-400 hover:text-green-600 disabled:cursor-default disabled:hover:text-gray-400"
                  title={
                    executorCanTick
                      ? m.is_done
                        ? "Wykonane — cofnąć może tylko zlecający"
                        : "Oznacz jako ukończony"
                      : m.is_done
                        ? "Oznacz jako niewykonane"
                        : "Oznacz jako wykonane"
                  }
                >
                  {m.is_done ? (
                    <CheckCircle2 size={16} className="text-green-600" />
                  ) : (
                    <Circle size={16} />
                  )}
                </button>
                <span className={m.is_done ? "line-through text-gray-400" : ""}>{m.label}</span>
                {/* Kłódka mówi „nie da się tego usunąć" — czytelne tylko dla kogoś, kto w ogóle
                    może usuwać. Podwykonawcy (read-only) nie pokazujemy wewnętrznego żargonu. */}
                {m.is_principal && canManage && (
                  <span title="Checkpoint pryncypialny (nieusuwalny)" className="shrink-0">
                    <Lock size={11} className="text-gray-400" />
                  </span>
                )}
                <span className="ml-auto flex items-center gap-2">
                  {m.is_done ? (
                    // Done → completion date is the fact; the planned term is no longer editable.
                    // Next to it: who ticked the milestone (from the milestone audit).
                    <span
                      className="text-green-600 dark:text-green-400"
                      title={m.completed_by_name ? `Oznaczono jako wykonane: ${m.completed_by_name}` : undefined}
                    >
                      {formatDate(m.completed_at ?? m.due_date)}
                      {m.completed_by_name && (
                        <span className="text-gray-500 dark:text-gray-400"> · {m.completed_by_name}</span>
                      )}
                    </span>
                  ) : canManage ? (
                    <DatePicker
                      value={m.due_date}
                      disabled={update.isPending}
                      onChange={(v) => changeDue(m, v)}
                      className={
                        overdue
                          ? "border-red-400 text-red-600 dark:text-red-400 font-medium"
                          : "text-gray-600 dark:text-gray-300"
                      }
                    />
                  ) : (
                    <span
                      className={
                        overdue
                          ? "text-red-600 dark:text-red-400 font-medium"
                          : "text-gray-500 dark:text-gray-400"
                      }
                    >
                      {formatDate(m.due_date)}
                    </span>
                  )}
                  {canManage && canEditList && milestones.length > 1 && (
                    <span className="flex flex-col -my-1">
                      <button
                        type="button"
                        disabled={i === 0 || move.isPending}
                        onClick={() => move.mutate({ milestoneId: m.id, direction: "up" })}
                        className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30 disabled:hover:text-gray-400"
                        title="Przesuń w górę"
                      >
                        <ChevronUp size={13} />
                      </button>
                      <button
                        type="button"
                        disabled={i === visible.length - 1 || move.isPending}
                        onClick={() => move.mutate({ milestoneId: m.id, direction: "down" })}
                        className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30 disabled:hover:text-gray-400"
                        title="Przesuń w dół"
                      >
                        <ChevronDown size={13} />
                      </button>
                    </span>
                  )}
                  {canManage && canEditList && !m.is_principal && (
                    <button
                      type="button"
                      onClick={() => handleDelete(m)}
                      className="text-gray-400 hover:text-red-600"
                      title="Usuń kamień"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {canManage && !canEditList && (
        <p className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3 text-xs text-gray-500 dark:text-gray-400">
          Zamówienie jest zakończone — nie można już dodawać, usuwać ani zmieniać kolejności
          kamieni milowych.
        </p>
      )}

      {executorCanTick && confirmMilestone && (
        <Modal open title="Ukończenie kamienia" onClose={closeConfirm} size="sm">
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Oznaczyć kamień <strong>„{confirmMilestone.label}”</strong> jako ukończony?
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Kamień zostanie oznaczony jako wykonany z dzisiejszą datą. Cofnąć to może już
              tylko zlecający.
            </p>
            {complete.isError && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {(complete.error as { response?: { data?: { errors?: { detail?: string } } } })
                  ?.response?.data?.errors?.detail || "Nie udało się oznaczyć kamienia."}
              </p>
            )}
            <div className="flex gap-3">
              <Button loading={complete.isPending} onClick={confirmComplete}>
                Potwierdź
              </Button>
              <Button variant="secondary" disabled={complete.isPending} onClick={closeConfirm}>
                Anuluj
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {canManage && canEditList && (
        <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3 flex flex-wrap items-end gap-2">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Nazwa kamienia"
            className="flex-1 min-w-[160px] text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1"
          />
          <DatePicker
            value={due}
            onChange={setDue}
          />
          <Button size="sm" loading={add.isPending} disabled={!label.trim() || !due} onClick={handleAdd}>
            <Plus size={14} /> Dodaj
          </Button>
          {error && <p className="w-full text-xs text-red-600 dark:text-red-400">{error}</p>}
        </div>
      )}

      {events.length > 0 && (
        <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
            <Clock size={13} /> Historia zmian
          </p>
          <ul className="space-y-1.5">
            {events.map((ev) => {
              const Icon = EVENT_ICON[ev.action] ?? Clock;
              return (
                <li key={ev.id} className="flex items-start gap-2 text-xs leading-snug">
                  <Icon size={13} className={`mt-0.5 shrink-0 ${EVENT_TONE[ev.action] ?? "text-gray-400"}`} />
                  <span className="text-gray-600 dark:text-gray-300">
                    <span className="font-medium text-gray-800 dark:text-gray-100">{ev.actor_name}</span>{" "}
                    {ev.message}
                    <span className="ml-1 text-gray-400 dark:text-gray-500">· {fmtDateTime(ev.created_at)}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
